/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DisaEntriesPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for DISA Entries management with list view.
    Displays DISA entries for Direct Inward System Access functionality.

 🔧 Notes:
    - Refactored from monolithic file
    - Uses useDisaEntryList hook for data management
    - Clean separation of concerns

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneCall, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { useContextStore } from '../../store/context';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useDisaEntryList } from './hooks/useDisaEntryList';
import { deleteDisaEntryById, navigateToDisaEntryDuplicate } from './disaEntriesActions';
import { DisaEntry } from './types';

export default function DisaEntriesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; entry: DisaEntry | null }>({
    isOpen: false,
    entry: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    getAvailableColumns
  } = useDisaEntryList({
    domainId: selectedDomain?.id,
  });

  // Add render functions to columns
  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      if (col.key === 'context') {
        return {
          ...col,
          render: (_value: unknown, item: DisaEntry) => (
            <span className="font-mono font-medium text-primary">{item?.context || '-'}</span>
          )
        };
      }
      if (col.key === 'classOfServiceName') {
        return {
          ...col,
          render: (_value: unknown, item: DisaEntry) => (
            <span className="text-sm text-muted-foreground">
              {item?.classOfServiceName || t('disaEntries:columns.none')}
            </span>
          )
        };
      }
      if (col.key === 'enabled') {
        return {
          ...col,
          render: (_value: unknown, item: DisaEntry) => (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}
            >
              {item?.enabled ? t('common:enabled') : t('common:disabled')}
            </span>
          )
        };
      }
      return col;
    });
  }, [baseColumns, t]);

  const handleAdd = useCallback(() => {
    navigate('/pbx/applications/disa/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (entry: DisaEntry) => {
      navigate(`/pbx/applications/disa/${entry.uuid || entry.id}`);
    },
    [navigate]
  );

  const handleDuplicate = useCallback(
    (entry: DisaEntry) => {
      navigateToDisaEntryDuplicate(navigate, String(entry.uuid || entry.id));
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((entry: DisaEntry) => {
    setDeleteModal({ isOpen: true, entry });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.entry) return;

    setIsDeleting(true);
    try {
      await deleteDisaEntryById(String(deleteModal.entry.uuid || deleteModal.entry.id));

      showSuccess(t('disaEntries:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, entry: null });
      refresh();
    } catch (error) {
      showError(error?.message || t('disaEntries:messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.entry, refresh, showError, showSuccess, t]);

  const renderRowActions = useCallback(
    (item: DisaEntry) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('disaEntries:actions.edit')} position="top">
          <button
            onClick={() => handleEdit(item)}
            className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('disaEntries:actions.duplicate')} position="top">
          <button
            onClick={() => handleDuplicate(item)}
            className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>
        <SimpleTooltip content={t('disaEntries:actions.delete')} position="top">
          <button
            onClick={() => handleDeleteClick(item)}
            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t]
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleAdd}
          title={t('disaEntries:title')}
          titleIcon={<PhoneCall className="h-5 w-5 text-primary" />}
          addButtonText={t('disaEntries:actions.add')}
          renderActions={renderRowActions}
          searchPlaceholder={t('disaEntries:searchPlaceholder')}
          emptyMessage={t('disaEntries:emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, entry: null })}
        onConfirm={handleDeleteConfirm}
        title={t('disaEntries:messages.deleteTitle')}
        message={t('disaEntries:messages.confirmDelete', { name: deleteModal.entry?.name || '' })}
        confirmText={t('disaEntries:actions.delete')}
        cancelText={t('disaEntries:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
